import { useDeleteConfirmation, useToast } from '@/app/providers';
import { DataTable, type ColumnDef } from '@/core/ui/components';
import { useAbility } from '@/modules/identity/application/ability';
import { useI18n } from '@/shared/i18n';
import { Select } from '@/presentation/components/atoms/Select';
import { Button } from '@/presentation/components/atoms/Button';
import { Switch } from '@/presentation/components/atoms/Switch';
import type { Routine, RoutineCategory } from '@yawi/types';
import { Edit2, History, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useRoutineActions } from '../hooks/useRoutineActions';
import { useRoutineList } from '../hooks/useRoutineList';
import { ExecutionListModal } from './ExecutionListModal';
import { RoutineFormModal } from './RoutineFormModal';
import { cronToText } from '../utils/cronToText';

function formatDate(iso?: string) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

const CATEGORY_OPTIONS = [
  { value: '', label: 'Todas las categorías' },
  { value: 'billing', label: 'Facturación' },
  { value: 'access', label: 'Acceso' },
  { value: 'gdpr', label: 'GDPR' },
  { value: 'restrictions', label: 'Restricciones' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'custom', label: 'Personalizado' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'true', label: 'Activa' },
  { value: 'false', label: 'Pausada' },
];

const KNOWN_CATEGORIES: RoutineCategory[] = ['billing', 'access', 'gdpr', 'restrictions', 'maintenance', 'custom'];

function deriveCategory(routine: Routine): RoutineCategory | 'custom' {
  if (routine.category && KNOWN_CATEGORIES.includes(routine.category)) return routine.category;
  const prefix = (routine.action ?? '').split('.')[0];
  if (prefix && KNOWN_CATEGORIES.includes(prefix as RoutineCategory)) return prefix as RoutineCategory;
  return 'custom';
}

function categoryLabel(category: RoutineCategory | undefined | null, t: (key: string) => string) {
  if (category && KNOWN_CATEGORIES.includes(category)) return t(`routines.category.${category}`);
  return t('routines.category.uncategorized');
}

function routineFrequency(routine: Routine): string {
  if (routine.triggerSummary && routine.triggerSummary !== routine.cronExpression) return routine.triggerSummary;
  if (routine.cronExpression) return cronToText(routine.cronExpression);
  return '-';
}

function SwitchLoading() {
  return (
    <span className="inline-flex w-[36px] h-[20px] items-center justify-center" title="Actualizando...">
      <Loader2 size={16} className="animate-spin text-slate-400" />
    </span>
  );
}

export const RoutineList: React.FC = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const { confirmDelete } = useDeleteConfirmation();
  const toast = useToast();

  const {
    data: routines,
    loading,
    meta,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    filters,
    setFilters,
    refresh,
  } = useRoutineList();

  const { toggleRoutine, deleteRoutine, triggerRoutine, loadingId } = useRoutineActions(refresh);

  const [editingRoutine, setEditingRoutine] = useState<Routine | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [executionsRoutine, setExecutionsRoutine] = useState<Routine | null>(null);

  const canManageRoutine = ability.can('manage', 'all') || ability.can('manage', 'Routine');
  const canCreateRoutine = canManageRoutine || ability.can('create', 'Routine');
  const canUpdateRoutine = canManageRoutine || ability.can('update', 'Routine');
  const canDeleteRoutine = canManageRoutine || ability.can('delete', 'Routine');
  const canManageExecution = ability.can('manage', 'all') || ability.can('manage', 'RoutineExecution');
  const canCreateExecution = canManageExecution || ability.can('create', 'RoutineExecution');
  const canReadExecution = canManageExecution || ability.can('read', 'RoutineExecution');

  const handleDelete = (routine: Routine) => {
    if (!canDeleteRoutine) return;
    confirmDelete({
      itemName: routine.name,
      itemType: 'rutina',
      onConfirm: () => deleteRoutine(routine.id),
    });
  };

  const handleTrigger = async (routine: Routine) => {
    if (!canCreateExecution) return;
    const result = await triggerRoutine(routine.id);
    if (result) {
      toast.success('La rutina se ejecutó correctamente.', 'Rutina ejecutada');
    }
  };

  const columns: ColumnDef<Routine>[] = [
    {
      id: 'name',
      header: 'Nombre',
      cell: (row) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800 text-sm">{row.name}</span>
          </div>
          {row.description && <p className="text-xs text-slate-400 mt-0.5 max-w-xs truncate">{row.description}</p>}
        </div>
      ),
    },
    {
      id: 'category',
      header: 'Categoría',
      cell: (row) => (
        <span className="text-xs text-slate-500 capitalize">{categoryLabel(deriveCategory(row), t)}</span>
      ),
    },
    {
      id: 'triggerSummary',
      header: t('routines.triggerSummary'),
      cell: (row) => <span className="text-xs text-slate-600">{routineFrequency(row)}</span>,
    },
    {
      id: 'lastRun',
      header: t('routines.lastRun'),
      cell: (row) => (
        <div>
          <span className="text-xs text-slate-600">{formatDate(row.lastRunAt)}</span>
          {row.lastStatus && (
            <div className="mt-0.5">
              <span
                className={`text-xs ${row.lastStatus === 'success' ? 'text-emerald-600' : row.lastStatus === 'failed' ? 'text-red-500' : 'text-slate-400'}`}
              >
                {t(`routines.executionStatus.${row.lastStatus}`)}
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'nextRun',
      header: t('routines.nextRun'),
      cell: (row) => <span className="text-xs text-slate-500">{formatDate(row.nextRunAt)}</span>,
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (row) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            row.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {row.enabled ? t('routines.enabled') : t('routines.disabled')}
        </span>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={routines}
        keyExtractor={(r) => r.id}
        loading={loading}
        emptyMessage={t('routines.noRoutines')}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('routines.search')}
        filters={
          <div className="flex gap-2">
            <Select
              value={filters.category ?? ''}
              onChange={(val) => setFilters({ category: val as RoutineCategory | '' })}
              options={CATEGORY_OPTIONS}
            />
            <Select
              value={filters.enabled ?? ''}
              onChange={(val) => setFilters({ enabled: val as '' | 'true' | 'false' })}
              options={STATUS_OPTIONS}
            />
          </div>
        }
        actions={
          canCreateRoutine ? (
            <Button
              onClick={() => {
                setEditingRoutine(null);
                setIsFormOpen(true);
              }}
              icon={<Plus size={16} />}
            >
              {t('routines.create')}
            </Button>
          ) : undefined
        }
        rowActions={(routine) => (
          <div className="flex items-center gap-1">
            {canUpdateRoutine &&
              (loadingId === routine.id ? (
                <SwitchLoading />
              ) : (
                <Switch
                  checked={routine.enabled}
                  onChange={() => toggleRoutine(routine.id, !routine.enabled, routine.name)}
                />
              ))}
            {canReadExecution && (
              <button
                onClick={() => setExecutionsRoutine(routine)}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                title={t('routines.executions')}
              >
                <History size={15} />
              </button>
            )}
            {canCreateExecution && (
              <button
                onClick={() => handleTrigger(routine)}
                disabled={loadingId === routine.id}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-emerald-600"
                title={t('routines.runNow')}
              >
                <RefreshCw size={15} />
              </button>
            )}
            {canUpdateRoutine && (
              <button
                onClick={() => {
                  setEditingRoutine(routine);
                  setIsFormOpen(true);
                }}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-blue-600"
                title={t('routines.edit')}
              >
                <Edit2 size={15} />
              </button>
            )}
            {!routine.isSystem && canDeleteRoutine && (
              <button
                onClick={() => handleDelete(routine)}
                disabled={loadingId === routine.id}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-red-500"
                title={t('routines.delete')}
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        )}
      />

      {isFormOpen && ((editingRoutine && canUpdateRoutine) || (!editingRoutine && canCreateRoutine)) && (
        <RoutineFormModal
          routine={editingRoutine}
          onClose={() => setIsFormOpen(false)}
          onSuccess={refresh}
        />
      )}

      {executionsRoutine && canReadExecution && (
        <ExecutionListModal routine={executionsRoutine} onClose={() => setExecutionsRoutine(null)} />
      )}
    </>
  );
};
